'use client';

import { isoDate } from '@mnd-frontend/dates';
import { Sentiment } from '@mnd-frontend/graphql-schema';
import { Text } from '@mnd-frontend/ui/publish/components';
import {
  DANGER_500,
  GREY_200,
  GREY_400,
  NEUTRAL_WHITE,
  SUCCESS_500,
} from '@mnd-frontend/ui/shared/tokens';
import { Fragment, useState } from 'react';
import styled, { css } from 'styled-components';
import type { Report2PerceptionBenchmarkSentimentOverviewFragment } from '../../../../generated/types';
import { useReportStaticData, useReportTracking, useReportTranslation } from '../../hooks';
import { type HitsModalDetails, HitsModalSentiment, SkeletonBarChart } from '../common';
import { GraphContainer } from '../common/GraphContainer';
import { ScrollContainer } from '../common/ScrollContainer';

const getBarColor = ({ $type }: { $type: string }) => {
  switch ($type) {
    case 'positive':
      return SUCCESS_500;
    case 'neutral':
      return GREY_400;
    case 'negative':
      return DANGER_500;
    default:
      return NEUTRAL_WHITE;
  }
};

const BarLabelling = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.25rem;
  margin-bottom: 2.25rem;
`;

const BarLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const ColorBox = styled.div<{ $type: string }>`
  width: 1rem;
  height: 1rem;
  border-radius: 0.25rem;
  background-color: ${getBarColor};
`;

const BarsWrapper = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 1rem;
`;

const Label = styled.span`
  display: flex;
  align-items: center;
  max-width: 125px;
  width: 100%;
`;

const Bar = styled.div<{ $clickable: boolean }>`
  display: flex;
  flex-direction: row;
  height: 1.625rem;
  width: 100%;
  margin: auto;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  border-radius: 0.25rem;
  overflow: hidden;
  background-color: ${GREY_200};
`;

const AxisLabels = styled.div`
  display: flex;
  justify-content: space-between;
`;

const BarSegment = styled.div<{ $type: string; $percentage: number }>`
  width: ${({ $percentage }) => $percentage}%;
  background-color: ${getBarColor};
  &:hover {
    opacity: 0.7;
  }
`;

const Wrapper = styled.div<{ $disabled: boolean }>`
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

type HitsDetails = {
  searchProfileIds: string[] | null;
  details: HitsModalDetails;
};

type MetricsData = Report2PerceptionBenchmarkSentimentOverviewFragment['metrics'];

export const SentimentBenchmarkGraph = ({
  data,
  loading,
  disabled,
}: {
  data: MetricsData;
  loading: boolean;
  disabled: boolean;
}) => {
  const { t } = useReportTranslation();
  const { fromDate, toDate, brand: brandName } = useReportStaticData();
  const [hitsData, setHitsData] = useState<HitsDetails | null>(null);
  const { track } = useReportTracking();

  const showHitsModal = ({
    label,
    searchProfileIds,
    sentiment,
  }: {
    label: string;
    searchProfileIds: string[] | null;
    sentiment: Sentiment;
  }) => {
    track({
      event: 'Brand Reports: Clicked Sentiment Competitors graph',
    });
    setHitsData({
      searchProfileIds,
      details: { startAt: isoDate(fromDate), endAt: isoDate(toDate), label, filter: sentiment },
    });
  };

  const closeHitsModal = () => setHitsData(null);

  return (
    <GraphContainer data-testid="share-of-benchmark-graph">
      <ScrollContainer minWidth="35rem">
        {!loading && (
          <Wrapper $disabled={disabled}>
            <BarLabelling>
              <BarLabel>
                <ColorBox $type={'positive'} />
                <Text as="span" variant="reportLabelSmall" color="GREY_800">
                  {t('reports_sentiment-positive', { count: 1 })}
                </Text>
              </BarLabel>
              <BarLabel>
                <ColorBox $type={'neutral'} />
                <Text as="span" variant="reportLabelSmall" color="GREY_800">
                  {t('reports_sentiment-neutral', { count: 1 })}
                </Text>
              </BarLabel>
              <BarLabel>
                <ColorBox $type={'negative'} />
                <Text as="span" variant="reportLabelSmall" color="GREY_800">
                  {t('reports_sentiment-negative', { count: 1 })}
                </Text>
              </BarLabel>
            </BarLabelling>
            <BarsWrapper>
              {data.map(({ searchProfileIds, brand, sentiment }, i) => {
                if (sentiment === null) return null;
                const {
                  positiveSentimentPercent,
                  neutralSentimentPercent,
                  negativeSentimentPercent,
                } = sentiment;

                const noHits =
                  positiveSentimentPercent + neutralSentimentPercent + negativeSentimentPercent ===
                  0;

                return (
                  <Fragment key={brand + i}>
                    {/* To make the dummy data for upsell look better. Use real brand name instead of dummy name */}
                    <Label>
                      <Text as="span" variant="bodySmall" color="GREY_900">
                        {i === 0 ? brandName : brand}
                      </Text>
                    </Label>
                    <Bar $clickable={!noHits}>
                      <BarSegment
                        $type={'positive'}
                        $percentage={positiveSentimentPercent}
                        onClick={() =>
                          showHitsModal({
                            label: `${brand} positive`,
                            searchProfileIds,
                            sentiment: Sentiment.Positive,
                          })
                        }
                      />
                      <BarSegment
                        $type={'neutral'}
                        $percentage={neutralSentimentPercent}
                        onClick={() =>
                          showHitsModal({
                            label: `${brand} neutral`,
                            searchProfileIds,
                            sentiment: Sentiment.Neutral,
                          })
                        }
                      />
                      <BarSegment
                        $type={'negative'}
                        $percentage={negativeSentimentPercent}
                        onClick={() =>
                          showHitsModal({
                            label: `${brand} negative`,
                            searchProfileIds,
                            sentiment: Sentiment.Negative,
                          })
                        }
                      />
                    </Bar>
                  </Fragment>
                );
              })}

              <Label />
              <AxisLabels>
                {[...Array(11).keys()].map(value => (
                  <Text key={value} as="span" variant="reportLabelSmall" color="GREY_700">
                    {value * 10}
                  </Text>
                ))}
              </AxisLabels>
            </BarsWrapper>
          </Wrapper>
        )}
        {loading && <SkeletonBarChart />}
      </ScrollContainer>
      {hitsData && <HitsModalSentiment onClose={closeHitsModal} {...hitsData} />}
    </GraphContainer>
  );
};
